import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { posthog } from 'posthog-js';

import { RouteParams } from '../library/types';
import { RootState } from '../../reducers';
import { AppDispatch } from '../../store';
import { FileGridItemShell } from '../library/files/components/FileGridItemShell';
import { FileListItemShell } from '../library/files/components/FileListItemShell';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { cancelUpload } from './uploadSlice';
import getTranslation from '../../i18n/utils';
import { abortMultipartUploadKeepalive } from '../../recorder/uploadService';

interface PendingFilesProps {
  view?: 'grid' | 'list';
}

const GridViewItem = ({
  progress,
  fileName,
  previewUrl,
  fileType,
  onCancel,
}: {
  progress: number;
  fileName: string;
  previewUrl?: string;
  fileType?: string;
  onCancel: () => void;
}) => {
  return (
    <FileGridItemShell
      name={fileName}
      thumbnailUrl={previewUrl}
      statusText={getTranslation('common.uploading')}
      progress={progress}
      showPercentage
      fileType={fileType}
      onCancel={onCancel}
    />
  );
};

const ListViewItem = ({
  progress,
  fileName,
  previewUrl,
  fileType,
  onCancel,
}: {
  progress: number;
  fileName: string;
  previewUrl?: string;
  fileType?: string;
  onCancel: () => void;
}) => {
  return (
    <FileListItemShell
      name={fileName}
      thumbnailUrl={previewUrl}
      statusText={getTranslation('common.uploading')}
      progress={progress}
      showPercentage
      fileType={fileType}
      onCancel={onCancel}
    />
  );
};

const PendingFiles = ({ view = 'list' }: PendingFilesProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { upload } = useSelector((state: RootState) => state);
  const { folderId } = useParams<RouteParams>();
  const { activeUploads } = upload;

  const [confirmFileId, setConfirmFileId] = useState<string | null>(null);

  const filteredFiles = activeUploads.filter((file) => {
    if (
      folderId === 'recents' ||
      folderId === 'private' ||
      folderId === 'favorites'
    )
      return true;
    return file.folderId === folderId;
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (filteredFiles.length > 0) {
        filteredFiles.forEach((file) => {
          if (!file.isMultipart) {
            return;
          }

          abortMultipartUploadKeepalive({
            teamId: file.teamId,
            folderId: file.folderId,
            fileId: file.fileId,
            contentType: file.fileType || 'video/x-matroska',
            reasonCode: 'browser_closed_during_file_upload',
            reason: 'Browser window closed during file upload',
            reasonDetail: 'beforeunload',
          });
        });

        event.preventDefault();
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [filteredFiles]);

  const handleCancelRequest = useCallback((fileId: string) => {
    setConfirmFileId(fileId);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (confirmFileId) {
      const file = activeUploads.find((f) => f.fileId === confirmFileId);
      posthog.capture('upload-cancelled', {
        fileType: file?.fileType,
        progress: file?.progress,
        isMultipart: file?.isMultipart,
      });
      dispatch(cancelUpload({ fileId: confirmFileId }));
      setConfirmFileId(null);
    }
  }, [confirmFileId, dispatch, activeUploads]);

  if (filteredFiles.length === 0) return null;

  const confirmDialog = (
    <ConfirmDialog
      open={confirmFileId !== null}
      onOpenChange={(open) => {
        if (!open) setConfirmFileId(null);
      }}
      title={getTranslation('upload.cancel.dialogTitle')}
      message={getTranslation('upload.cancel.dialogMessage')}
      confirmLabel={getTranslation('upload.cancel.confirm')}
      cancelLabel={getTranslation('common.cancel')}
      onConfirm={handleConfirmCancel}
      variant="destructive"
    />
  );

  if (view === 'grid') {
    return (
      <>
        {confirmDialog}
        {filteredFiles.map(
          ({ fileId, progress, fileName, previewUrl, fileType }) => (
            <GridViewItem
              key={fileId}
              progress={progress}
              fileName={fileName}
              previewUrl={previewUrl}
              fileType={fileType}
              onCancel={() => handleCancelRequest(fileId)}
            />
          ),
        )}
      </>
    );
  }

  return (
    <>
      {confirmDialog}
      {filteredFiles.map(
        ({ fileId, progress, fileName, previewUrl, fileType }) => (
          <ListViewItem
            key={fileId}
            progress={progress}
            fileName={fileName}
            previewUrl={previewUrl}
            fileType={fileType}
            onCancel={() => handleCancelRequest(fileId)}
          />
        ),
      )}
    </>
  );
};

export default PendingFiles;
